import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { PeekStack } from "@piiqtechnologies/ui-components";
import { CompetitorRow } from "./TopRankingWidgetComponents";
import { WidgetLoader } from "../widget-loader";
import { useError, useGetAiDiscoverabilityReport, useGetTopCompetitors, useGetWhiskerDistribution, } from "@piiqtechnologies/ui-lib";
import { WhiskerRangeChart } from "./WhiskerRangeChart";
import { RankingWidgetShell } from "../ranking-widget";
const EMPTY_COMPETITORS = Array.from({ length: 5 }, (_, i) => ({
    subjectId: "",
    subjectName: "-",
    appearanceRate: 0,
    bestPerformingSubcategories: [],
    mostCitedBy: [],
}));
// The subject's own details come from its report, since the top-competitors payload only
// carries them for competitors. Citations read from topSources (the field the Top Sources
// widgets use) with citingSources as a fallback for older payloads.
const buildSelfDetails = (report) => {
    var _a, _b, _c;
    const fromTopSources = ((_a = report.topSources) !== null && _a !== void 0 ? _a : [])
        .filter((s) => s.yourCitations > 0)
        .sort((a, b) => b.yourCitations - a.yourCitations)
        .map((s) => ({ source: s.source, count: s.yourCitations }));
    const fromCitingSources = ((_b = report.citingSources) !== null && _b !== void 0 ? _b : [])
        .filter((s) => s.count > 0)
        .sort((a, b) => b.count - a.count)
        .map((s) => ({ source: s.source, count: s.count }));
    return {
        bestPerformingSubcategories: ((_c = report.subcategoryBreakdown) !== null && _c !== void 0 ? _c : [])
            .flatMap((category) => { var _a; return (_a = category.subcategories) !== null && _a !== void 0 ? _a : []; })
            .filter((s) => typeof s.appearanceRate === "number" && s.appearanceRate > 0)
            .sort((a, b) => b.appearanceRate - a.appearanceRate)
            .map((s) => ({ subcategory: s.subcategory, appearanceRate: s.appearanceRate })),
        mostCitedBy: fromTopSources.length ? fromTopSources : fromCitingSources,
    };
};
// The subject belongs in the list only when its appearance rate beats at least one of the
// rows the API returned and the API did not already include it. The insertion point is
// derived from the value, NOT the benchmark's yourRank: the benchmark ranks a different
// competitor population than topCompetitors (the backend applies different filters to
// each), so that rank is not a valid index into this list. Shared by the splice below and
// by the widget to decide whether the report (which only feeds the self row) is needed.
const getInsertionIndex = (data, distribution) => {
    var _a, _b;
    const competitors = (_a = data === null || data === void 0 ? void 0 : data.topCompetitors) !== null && _a !== void 0 ? _a : [];
    if (!data || !competitors.length)
        return null;
    const alreadyIncluded = competitors.some((c) => c.isSelf || c.subjectId === data.subjectId);
    const yourValue = (_b = distribution === null || distribution === void 0 ? void 0 : distribution.appearanceRateBenchmark) === null || _b === void 0 ? void 0 : _b.yourValue;
    if (alreadyIncluded || yourValue == null)
        return null;
    // topCompetitors arrives sorted by appearanceRate descending.
    const index = competitors.findIndex((c) => c.appearanceRate < yourValue);
    return index === -1 ? null : index;
};
// The API's top list excludes the subject itself, so splice it in where its score slots
// into the shown rows (dropping the last row to keep the list length). The list stays in
// descending score order; the balloon's benchmark rank can differ from the row's position
// when the two backend populations disagree. `pendingSelfId` marks a spliced-in row whose
// report has not resolved, so its detail lines can show a placeholder instead of falsely
// claiming "None".
const insertSubjectRow = (data, distribution, selfDetails) => {
    var _a, _b, _c;
    const competitors = (_a = data === null || data === void 0 ? void 0 : data.topCompetitors) !== null && _a !== void 0 ? _a : [];
    const insertAt = getInsertionIndex(data, distribution);
    const benchmark = distribution === null || distribution === void 0 ? void 0 : distribution.appearanceRateBenchmark;
    if (!data || insertAt == null || !benchmark)
        return { rows: competitors };
    const selfRow = {
        subjectId: data.subjectId,
        subjectName: data.subjectName,
        appearanceRate: benchmark.yourValue,
        isSelf: true,
        bestPerformingSubcategories: (_b = selfDetails === null || selfDetails === void 0 ? void 0 : selfDetails.bestPerformingSubcategories) !== null && _b !== void 0 ? _b : [],
        mostCitedBy: (_c = selfDetails === null || selfDetails === void 0 ? void 0 : selfDetails.mostCitedBy) !== null && _c !== void 0 ? _c : [],
    };
    const rows = [...competitors.slice(0, insertAt), selfRow, ...competitors.slice(insertAt)].slice(0, competitors.length);
    return { rows, pendingSelfId: selfDetails ? undefined : data.subjectId };
};
export const TopRankingWidget = ({ subjectId, isCompleted, whiskerData, assessmentId, reportData, selfReportId, selfReportIdLoading, }) => {
    const intl = useIntl();
    const { setError } = useError();
    const [fetchTopCompetitors, topCompetitorsData, competitorsLoading] = useGetTopCompetitors();
    const [fetchWhiskerData, fetchedWhiskerData, whiskerLoading] = useGetWhiskerDistribution();
    const [fetchReport, fetchedReport, , reportError] = useGetAiDiscoverabilityReport();
    useEffect(() => {
        if (subjectId) {
            fetchTopCompetitors(subjectId, assessmentId);
            fetchWhiskerData(subjectId, assessmentId);
        }
    }, [subjectId, isCompleted, assessmentId, fetchTopCompetitors, fetchWhiskerData]);
    const reportFetchId = assessmentId !== null && assessmentId !== void 0 ? assessmentId : selfReportId;
    const distributionData = fetchedWhiskerData !== null && fetchedWhiskerData !== void 0 ? fetchedWhiskerData : whiskerData;
    const insertionIndex = getInsertionIndex(topCompetitorsData, distributionData);
    // When navigating between communities without unmounting, the ranking payloads lag one
    // render behind the new subjectId — never gate the report fetch on another community's
    // ranking.
    const payloadsMatchSubject = !!subjectId && (topCompetitorsData === null || topCompetitorsData === void 0 ? void 0 : topCompetitorsData.subjectId) === subjectId && (distributionData === null || distributionData === void 0 ? void 0 : distributionData.subjectId) === subjectId;
    useEffect(() => {
        // The report is the heaviest payload in the app and only feeds the self row's detail
        // lines, so it is fetched only once the subject is known to appear in the list.
        // reportData === null means the page owns the report and it is still loading;
        // only fetch when the prop was not supplied at all.
        if (payloadsMatchSubject && insertionIndex != null && reportFetchId && reportData === undefined) {
            fetchReport(reportFetchId);
        }
    }, [payloadsMatchSubject, insertionIndex, reportFetchId, reportData, fetchReport]);
    useEffect(() => {
        if (reportError)
            setError(reportError.message);
    }, [reportError, setError]);
    const report = reportData !== null && reportData !== void 0 ? reportData : fetchedReport;
    // The report never gates rendering: rows appear immediately and the self row's details
    // fill in when it resolves.
    const loading = competitorsLoading || whiskerLoading;
    if (loading) {
        return (React.createElement(PeekStack, { id: "toprank" },
            React.createElement(WidgetLoader, null)));
    }
    const selfDetails = report ? buildSelfDetails(report) : undefined;
    const { rows: rankingData, pendingSelfId } = insertSubjectRow(topCompetitorsData, distributionData, selfDetails);
    // Skeleton while the report is on its way: own fetch pending, the page's copy still
    // loading, or the page still resolving which report id to use. Hidden only once it is
    // certain there is nothing to load (fetch failed, or no report id after resolution).
    const selfDetailsPending = !report &&
        !reportError &&
        (reportData === null || (reportData === undefined && (!!reportFetchId || !!selfReportIdLoading)));
    const isEmpty = !rankingData.length && !distributionData;
    const displayRows = rankingData.length ? rankingData : EMPTY_COMPETITORS;
    const chart = !isEmpty ? ((distributionData === null || distributionData === void 0 ? void 0 : distributionData.appearanceRateBenchmark) ? (React.createElement(WhiskerRangeChart, { distribution: distributionData.appearanceRateBenchmark.distribution, yourValue: distributionData.appearanceRateBenchmark.yourValue, yourRank: distributionData.appearanceRateBenchmark.yourRank, totalCompetitors: distributionData.appearanceRateBenchmark.totalCompetitors })) : (React.createElement(WhiskerRangeChart, null))) : undefined;
    return (React.createElement(RankingWidgetShell, { id: "toprank", chart: chart, isEmpty: isEmpty }, displayRows.map((row, index) => (React.createElement(CompetitorRow, { key: row.subjectId || index, competitor: row, rank: index + 1, hideDetails: !!pendingSelfId && row.subjectId === pendingSelfId && !selfDetailsPending, detailsLoading: !!pendingSelfId && row.subjectId === pendingSelfId && selfDetailsPending })))));
};
export default TopRankingWidget;
